import React, { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useBreadcrumbs } from "@/components/provider/BreadcrumbsProvider";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import OrgActivityLog from "@/model/interface/OrgActivityLog";
import { useGetActivityLogQuery } from "@/store/rest_api";

const CATEGORIES: { value: string; label: string }[] = [
  { value: "all", label: "All" },
  { value: "CRM_SYNC", label: "CRM Sync" },
  { value: "WORKFLOW_RUN", label: "Workflow Run" },
  { value: "CRM_PUSH", label: "CRM Push" },
  { value: "IMPORT", label: "Import" },
  { value: "EXPORT", label: "Export" },
  { value: "INTEGRATION", label: "Integration" },
  { value: "CALL_DISPOSITION", label: "Disposition" },
  { value: "CALL_SCRIPT", label: "Call Script" },
  { value: "AUDIO_CLIP", label: "Audio Clip" },
];

const categoryLabel = (value: string) => CATEGORIES.find((c) => c.value === value)?.label ?? value;

export default function ActivityLogPage() {
  const { setSection } = useBreadcrumbs();
  const [category, setCategory] = useState<string>("all");
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(25);

  const { data, isFetching, refetch } = useGetActivityLogQuery({
    category: category === "all" ? undefined : category,
    page,
    size,
  });

  useEffect(() => {
    setSection("Activity");
  }, []);

  const statusBadge = (status: string) => {
    switch (status) {
      case "FAILED":
        return <Badge variant="destructive">Failed</Badge>;
      case "INFO":
        return <Badge variant="secondary">Info</Badge>;
      default:
        return <Badge variant="default">Success</Badge>;
    }
  };

  return (
    <div className="p-4 text-foreground">
      <div className="mb-6">
        <h1 className="text-3xl font-bold tracking-tight">Activity</h1>
        <p className="text-muted-foreground">
          CRM syncs, workflow runs, pushes, imports, integration changes, and who changed dispositions, call scripts
          and audio clips.
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Activity Log</CardTitle>
              <CardDescription>Recent events across your organisation</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Select
                value={category}
                onValueChange={(value) => {
                  setCategory(value);
                  setPage(0);
                }}
              >
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c.value} value={c.value}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
                {isFetching ? "Refreshing..." : "Refresh"}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Category</TableHead>
                <TableHead>Event</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Details</TableHead>
                <TableHead>Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isFetching && (!data || data.items.length === 0) && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground">
                    Loading...
                  </TableCell>
                </TableRow>
              )}
              {data?.items.map((log: OrgActivityLog) => (
                <TableRow key={log.id}>
                  <TableCell>
                    <Badge variant="outline">{categoryLabel(log.category)}</Badge>
                  </TableCell>
                  <TableCell className="font-medium">{log.title}</TableCell>
                  <TableCell>{statusBadge(log.status)}</TableCell>
                  <TableCell
                    className="text-xs text-muted-foreground max-w-[280px] truncate"
                    title={log.message ?? undefined}
                  >
                    {log.message || "—"}
                  </TableCell>
                  <TableCell className="text-xs whitespace-nowrap">
                    {new Date(log.createdAt).toLocaleString()}
                  </TableCell>
                </TableRow>
              ))}
              {!isFetching && (!data?.items || data.items.length === 0) && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground">
                    No activity yet
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {data && data.totalElements > 0 && (
            <div className="flex items-center justify-between mt-4">
              <div className="flex items-center gap-3">
                <span className="text-sm text-muted-foreground">
                  {data.totalElements} {data.totalElements === 1 ? "event" : "events"}
                  {data.totalPages > 1 && ` · Page ${page + 1} of ${data.totalPages}`}
                </span>
                <Select
                  value={String(size)}
                  onValueChange={(value) => {
                    setSize(Number(value));
                    setPage(0);
                  }}
                >
                  <SelectTrigger className="w-25">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {data.totalPages > 1 && (
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(page - 1)}>
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Previous
                  </Button>
                  <Button variant="outline" size="sm" disabled={data.last} onClick={() => setPage(page + 1)}>
                    Next
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
